import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../Home/HomePage.module.css";

const API = "https://simplix.co.za/api";
//const API = "http://127.0.0.1:8000/api";

const money = (value) => `R${Number(value || 0).toFixed(2)}`;

const createOperationId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    },
  );
};
const authenticatedFetch = (url, options = {}) => {
  const token =
    localStorage.getItem("auth_token") || localStorage.getItem("token");
  if (!token) {
    window.location.href = "/#/login";
    throw new Error("No auth token");
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
};

export default function MeterSettings() {
  const { meterId } = useParams();
  const navigate = useNavigate();

  const [meter, setMeter] = useState(null);
  const [paidType, setPaidType] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [pageError, setPageError] = useState("");
  const [success, setSuccess] = useState("");

  const [confirmAction, setConfirmAction] = useState(null);
  const [reason, setReason] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [converting, setConverting] = useState(false);

  const email = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();

  const loadSettings = useCallback(async () => {
    if (!email) {
      navigate("/#/login", { replace: true });
      return;
    }

    setLoading(true);
    setPageError("");
    const controller = new AbortController();

    try {
      const response = await authenticatedFetch(
        `${API}/meters/${meterId}/settings/`, // No email parameter
        { signal: controller.signal },
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Could not load meter settings");
      }

      setMeter(data.meter);
      setPaidType(String(data.meter.paid_type ?? ""));
    } catch (requestError) {
      setMeter(null);
      setPageError(
        "We could not load the meter settings. Please refresh the page and try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [email, meterId, navigate]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const savePaidType = async () => {
    if (!paidType) {
      setError("Select a paid type");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await authenticatedFetch(
        `${API}/meters/${meterId}/settings/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paid_type: Number(paidType),
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Could not update the paid type");
      }

      setMeter(data.meter);
      setPaidType(String(data.meter.paid_type ?? ""));
      setSuccess("Paid type updated successfully.");
    } catch (requestError) {
      setError(requestError.message || "Could not update the paid type");
    } finally {
      setSaving(false);
    }
  };

  const confirmConversion = async () => {
    setConverting(true);
    setError("");
    setSuccess("");
    try {
      const response = await authenticatedFetch(
        `${API}/meters/${meterId}/settings/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paid_type: 100000000,
          }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok)
        throw new Error(data.error || "Could not convert the paid type");
      setShowTermsModal(false);
      setMeter(data.meter);
      setPaidType(String(data.meter.paid_type ?? ""));
      setSuccess("Meter successfully converted to Prepaid model.");
    } catch (e) {
      setError(e.message || "Could not convert the paid type");
    } finally {
      setConverting(false);
    }
  };

  const openActionConfirmation = (action) => {
    setError("");
    setSuccess("");
    setReason("");
    setConfirmAction(action);
  };

  const closeActionConfirmation = () => {
    if (processing) return;

    setConfirmAction(null);
    setReason("");
  };

  const confirmRelayAction = async () => {
    if (confirmAction === "disconnect" && !reason.trim()) {
      setError("A reason for disconnection is required.");
      return;
    }

    setProcessing(true);
    setError("");
    setSuccess("");

    const operationId = createOperationId();

    try {
      const response = await authenticatedFetch(
        `${API}/meters/${meterId}/relay-action/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: confirmAction,
            reason: confirmAction === "disconnect" ? reason.trim() : "",
            operation_id: operationId,
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || "The meter action could not be completed",
        );
      }

      setConfirmAction(null);
      setReason("");
      setSuccess(data.message || "Meter updated successfully.");

      await loadSettings();
    } catch (requestError) {
      setError(
        requestError.message || "The meter action could not be completed",
      );
    } finally {
      setProcessing(false);
    }
  };

  if (pageError) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateIcon}>!</div>
          <h2>Meter settings unavailable</h2>
          <p>{pageError}</p>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => window.location.reload()}
          >
            Refresh and retry
          </button>

          <button
            type="button"
            className={styles.paymentStateBack}
            onClick={() => navigate("/meters")}
          >
            Return to meters
          </button>
        </div>
      </div>
    );
  }

  if (loading || saving || processing) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateSpinner} />

          <h2>
            {processing
              ? "Updating meter"
              : saving
                ? "Saving paid type"
                : "Loading meter settings"}
          </h2>

          <p>
            {processing
              ? "Please wait while the meter action is completed and verified."
              : saving
                ? "Please wait while the paid type and audit note are saved."
                : "We are checking the meter, wallet and available settings."}
          </p>

          <strong>Please do not close or refresh this page.</strong>
        </div>
      </div>
    );
  }

  if (!meter) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateIcon}>!</div>
          <h2>Meter unavailable</h2>
          <p>The meter settings could not be loaded.</p>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => navigate("/meters")}
          >
            Return to meters
          </button>
        </div>
      </div>
    );
  }

  const canReconnect = meter.is_off && meter.wallet_in_credit;
  const paidTypeChanged =
    paidType && Number(paidType) !== Number(meter.paid_type);

  return (
    <div className={styles.meterSettingsPage}>
      <div className={styles.meterSettingsShell}>
        <button
          type="button"
          className={styles.meterSettingsBack}
          onClick={() => navigate("/meters")}
        >
          <span aria-hidden="true">←</span>
          Back to meters
        </button>

        <header className={styles.meterSettingsHero}>
          <div className={styles.meterSettingsHeroContent}>
            <div>
              <p className={styles.meterSettingsEyebrow}>Meter settings</p>

              <h1 className={styles.meterSettingsNumber}>{meter.number}</h1>

              <div className={styles.meterSettingsLocation}>
                <span>{meter.unit || "Unit not available"}</span>
                <span aria-hidden="true">•</span>
                <span>{meter.building || "Building not available"}</span>
              </div>
            </div>

            <div
              className={`${styles.meterSettingsRelayPill} ${
                meter.is_off
                  ? styles.meterSettingsRelayOff
                  : styles.meterSettingsRelayOn
              }`}
            >
              <span className={styles.meterSettingsRelayDot} />
              Power {meter.is_off ? "off" : "on"}
            </div>
          </div>
        </header>

        {error && (
          <div
            className={`${styles.meterSettingsAlert} ${styles.meterSettingsAlertError}`}
            role="alert"
          >
            <strong>Could not complete the request</strong>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div
            className={`${styles.meterSettingsAlert} ${styles.meterSettingsAlertSuccess}`}
            role="status"
          >
            <strong>Update completed</strong>
            <span>{success}</span>
          </div>
        )}

        <main className={styles.meterSettingsGrid}>
          <section className={styles.meterSettingsCard}>
            <div className={styles.meterSettingsCardHeader}>
              <div className={styles.meterSettingsIcon}>₿</div>

              <div>
                <h2>Billing settings</h2>
                <p>Manage how this meter is billed.</p>
              </div>
            </div>

            <div className={styles.meterSettingsCardBody}>
              <div className={styles.meterSettingsField}>
                <label htmlFor="meter-paid-type">Paid type</label>

                {meter.paid_type === 100000001 && (
                  <>
                    <button
                      type="button"
                      className={styles.meterSettingsPrimaryButton}
                      disabled={converting || processing}
                      onClick={() => setShowTermsModal(true)}
                    >
                      ⇄ Change Paid Type to Prepaid
                    </button>
                    <p
                      className={styles.meterSettingsFieldHint}
                      style={{ marginTop: "0.5rem" }}
                    >
                      Converting to Prepaid is permanent and cannot be reversed.
                    </p>
                  </>
                )}

                {meter.paid_type === 100000000 && (
                  <div
                    className={styles.meterSettingsBlocked}
                    style={{ marginTop: "1rem" }}
                  >
                    <strong>Prepaid model is active.</strong>
                    <span>
                      This meter operates under the Prepaid model. This election
                      is permanent and cannot be changed.
                    </span>
                  </div>
                )}

                <p className={styles.meterSettingsFieldHint}>
                  Current type:{" "}
                  <strong>{meter.paid_type_label || "Not configured"}</strong>
                </p>
                <div
                  className={styles.meterSettingsPaidTypeGuide}
                  aria-label="Paid type explanations"
                >
                  <div
                    className={`${styles.meterSettingsPaidTypeOption} ${
                      paidType === "100000000"
                        ? styles.meterSettingsPaidTypeOptionSelected
                        : ""
                    }`}
                  >
                    <div className={styles.meterSettingsPaidTypeOptionHeader}>
                      <strong>Prepaid</strong>
                      <span>Daily balance check</span>
                    </div>

                    <p>
                      The system will check the wallet balance daily. If the
                      wallet has a debit balance at 11:00 a.m., the relevant
                      party will be notified. If the balance is not corrected by
                      3:00 p.m., the meter will be disconnected.
                    </p>
                  </div>

                  <div
                    className={`${styles.meterSettingsPaidTypeOption} ${
                      paidType === "100000001"
                        ? styles.meterSettingsPaidTypeOptionSelected
                        : ""
                    }`}
                  >
                    <div className={styles.meterSettingsPaidTypeOptionHeader}>
                      <strong>Postpaid</strong>
                      <span>Monthly balance check</span>
                    </div>

                    <p>
                      The system will check the wallet balance once a month
                      {meter.disconnection_day
                        ? `, on day ${meter.disconnection_day} of each month`
                        : " on the building’s configured disconnection day"}
                      . If the wallet has a debit balance at 11:00 a.m. on that
                      day, the relevant party will be notified. If the balance
                      is not corrected by 3:00 p.m., the meter will be
                      disconnected.
                    </p>
                  </div>
                </div>
              </div>

              {/*<button
                type="button"
                className={styles.meterSettingsPrimaryButton}
                disabled={saving || processing || !paidTypeChanged}
                onClick={savePaidType}
              >
                {saving ? "Updating…" : "Update paid type"}
              </button>*/}

              <div className={styles.meterSettingsDivider} />

              <div className={styles.meterSettingsPackage}>
                <span className={styles.meterSettingsLabel}>
                  Building package
                </span>

                <strong>
                  {meter.selected_package_label ||
                    meter.selected_package ||
                    "Not configured"}
                </strong>

                {meter.show_gma_link && (
                  <a
                    href={meter.gma_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.meterSettingsExternalLink}
                  >
                    Link meter to levies on GMA
                    <span aria-hidden="true">↗</span>
                  </a>
                )}
              </div>
            </div>
          </section>

          <section
            className={`${styles.meterSettingsCard} ${styles.meterSettingsPowerCard}`}
          >
            <div className={styles.meterSettingsCardHeader}>
              <div
                className={`${styles.meterSettingsIcon} ${
                  meter.is_off
                    ? styles.meterSettingsIconOff
                    : styles.meterSettingsIconOn
                }`}
              >
                ⏻
              </div>

              <div>
                <h2>Power control</h2>
                <p>Control the meter relay and power supply.</p>
              </div>
            </div>

            <div className={styles.meterSettingsCardBody}>
              <div className={styles.meterSettingsStats}>
                <div className={styles.meterSettingsStat}>
                  <span>Relay state</span>

                  <strong
                    className={
                      meter.is_off
                        ? styles.meterSettingsValueOff
                        : styles.meterSettingsValueOn
                    }
                  >
                    {meter.relay_state}
                  </strong>
                </div>

                <div className={styles.meterSettingsStat}>
                  <span>Wallet balance</span>
                  <strong>{money(meter.wallet_balance)}</strong>
                </div>

                {meter.is_off && (
                  <div className={styles.meterSettingsStat}>
                    <span>Wallet status</span>

                    <strong
                      className={
                        meter.wallet_in_credit
                          ? styles.meterSettingsValueOn
                          : styles.meterSettingsValueOff
                      }
                    >
                      {meter.wallet_in_credit ? "In credit" : "Not in credit"}
                    </strong>
                  </div>
                )}
              </div>

              <div className={styles.meterSettingsPowerMessage}>
                {meter.is_off ? (
                  <>
                    <strong>The meter is currently off.</strong>
                    <span>
                      Reconnection is available when the wallet is in credit.
                    </span>
                  </>
                ) : (
                  <>
                    <strong>The meter is currently active.</strong>
                    <span>
                      Disconnecting it will immediately switch off the power
                      supply.
                    </span>
                  </>
                )}
              </div>

              {!meter.is_off && (
                <button
                  type="button"
                  className={styles.meterSettingsDangerButton}
                  disabled={processing}
                  onClick={() => openActionConfirmation("disconnect")}
                >
                  <span aria-hidden="true">⏻</span>
                  Disconnect meter
                </button>
              )}

              {meter.is_off && (
                <>
                  <button
                    type="button"
                    className={styles.meterSettingsReconnectButton}
                    disabled={!canReconnect || processing}
                    onClick={() => openActionConfirmation("reconnect")}
                  >
                    <span aria-hidden="true">⏻</span>
                    Reconnect meter
                  </button>

                  {!meter.wallet_in_credit && (
                    <div className={styles.meterSettingsBlocked}>
                      The wallet must be in credit before this meter can be
                      reconnected.
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </main>
      </div>

      {confirmAction && (
        <div
          className={styles.meterSettingsModalBackdrop}
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeActionConfirmation();
            }
          }}
        >
          <div
            className={styles.meterSettingsModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="meter-action-title"
          >
            <div
              className={`${styles.meterSettingsModalIcon} ${
                confirmAction === "disconnect"
                  ? styles.meterSettingsModalIconDanger
                  : styles.meterSettingsModalIconSuccess
              }`}
            >
              ⏻
            </div>

            <h2 id="meter-action-title">
              {confirmAction === "disconnect"
                ? "Disconnect this meter?"
                : "Reconnect this meter?"}
            </h2>

            <p className={styles.meterSettingsModalIntro}>
              {confirmAction === "disconnect"
                ? "The meter will be switched off immediately."
                : "The meter will be switched on immediately."}
            </p>

            <div className={styles.meterSettingsFeeSummary}>
              <span>
                {confirmAction === "disconnect"
                  ? "Disconnection fee"
                  : "Reconnection fee"}
              </span>

              <strong>
                {money(
                  confirmAction === "disconnect"
                    ? meter.disconnection_fee
                    : meter.reconnection_fee,
                )}
              </strong>
            </div>

            {confirmAction === "disconnect" && (
              <div className={styles.meterSettingsModalField}>
                <label htmlFor="disconnect-reason">
                  Reason for disconnection
                </label>

                <textarea
                  id="disconnect-reason"
                  rows="4"
                  maxLength="500"
                  value={reason}
                  disabled={processing}
                  placeholder="Enter the reason for disconnecting this meter"
                  onChange={(event) => setReason(event.target.value)}
                />

                <span>{reason.length}/500</span>
              </div>
            )}

            <div className={styles.meterSettingsModalActions}>
              <button
                type="button"
                className={styles.meterSettingsCancelButton}
                disabled={processing}
                onClick={closeActionConfirmation}
              >
                Cancel
              </button>

              <button
                type="button"
                className={
                  confirmAction === "disconnect"
                    ? styles.meterSettingsDangerButton
                    : styles.meterSettingsReconnectButton
                }
                disabled={
                  processing ||
                  (confirmAction === "disconnect" && !reason.trim())
                }
                onClick={confirmRelayAction}
              >
                {processing
                  ? "Processing…"
                  : confirmAction === "disconnect"
                    ? "Confirm disconnection"
                    : "Confirm reconnection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div
          className={styles.meterSettingsModalBackdrop}
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !converting)
              setShowTermsModal(false);
          }}
        >
          <div
            className={styles.meterSettingsModal}
            role="dialog"
            aria-modal="true"
            style={{ maxWidth: "600px", overflowY: "auto", maxHeight: "85vh" }}
          >
            <div
              className={`${styles.meterSettingsModalIcon} ${styles.meterSettingsModalIconDanger}`}
            >
              ⚠
            </div>
            <h2>Change Paid Type — Important Notice</h2>
            <div style={{ fontSize: "0.9rem", lineHeight: 1.6 }}>
              I acknowledge that my property currently operates under the{" "}
              <strong>P4Power Postpaid Model</strong> and that I am being asked
              to consent to its conversion to the{" "}
              <strong>P4Power Prepaid Model</strong>.<br></br>I understand and
              accept that:<br></br>
              <br></br>
              1. Under the P4Power Prepaid Model, I will be required to maintain
              sufficient funds in my <strong>P4Power Wallet</strong> for amounts
              processed through my P4Power Account.<br></br>
              2. The P4Power Wallet may be used for the payment and allocation
              of utilities, ancillary charges, fees and other amounts lawfully
              processed through my P4Power Account in accordance with the
              applicable P4Power terms.<br></br>
              3. Any existing amount owing on my P4Power Account remains payable
              after conversion.<br></br>
              4.{" "}
              <strong>
                Once the conversion from the P4Power Postpaid Model to the
                P4Power Prepaid Model has been implemented, my election is
                permanent and cannot be reversed.
              </strong>
              <br></br>
              5. Where applicable, implementation remains subject to the
              required authority of the Body Corporate in terms of the Sectional
              Titles Schemes Management Act, applicable Prescribed Management
              Rules and the scheme's rules. PMR 29(4) specifically requires a{" "}
              <strong>special resolution</strong> for the installation/operation
              of separate prepaid meters on common property to control
              electricity or water supply to a section or exclusive-use area.
              <br></br>
              <br></br>
              Consent<br></br>
              <strong>
                I have read and understood the above. I voluntarily consent to
                my property being converted from the P4Power Postpaid Model to
                the P4Power Prepaid Model and acknowledge that, once
                implemented, the conversion cannot be reversed at my request.
                <br></br>I further consent to my approval being recorded and
                presented to the Body Corporate as evidence of my approval for
                the proposed conversion to a prepayment model.
              </strong>
            </div>
            <div className={styles.meterSettingsModalActions}>
              <button
                className={styles.meterSettingsCancelButton}
                disabled={converting}
                onClick={() => setShowTermsModal(false)}
              >
                Cancel
              </button>
              <button
                className={styles.meterSettingsDangerButton}
                disabled={converting}
                onClick={confirmConversion}
              >
                {converting ? "Processing…" : "I Accept — Convert to Prepaid"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
